import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperCore, {
  Swiper as SwiperType,
  EffectCards,
  Navigation
} from 'swiper';

import classNames from 'classnames';
import 'swiper/css';

import 'swiper/css/navigation';
import './styles.css';
import Button, {
  type Props as ButtonProps
} from '../../Atoms/Button';
import Icon from '../../Atoms/Icon';

interface Props {
  slides: JSX.Element[];
  utilityButton?: ButtonProps;
  loop?: boolean;
  navigationOutside?: boolean;
  navigationPosY?: 'bottom';
  slidesPerView?: number | 'auto';
  centeredSlides?: boolean;
}

export function MobileSlider({
  slides,
  navigationOutside = false,
  navigationPosY,
  utilityButton,
  slidesPerView,
  loop = false,
  centeredSlides = false
}: Props) {
  const lastSlideIndex = slides.length - 1;
  const [currentActiveSlide, setCurrentActiveSlide] =
    useState(0);
  const swiperRef = useRef<SwiperType>();
  return (
    <div
      className={classNames([
        'MOBILE',
        navigationOutside && !navigationPosY && 'lg:px-16',
        navigationOutside &&
          slidesPerView === 'auto' &&
          'lg:px-0',
        navigationPosY === 'bottom' && 'lg:px-0',
        'flex w-full max-w-[1320px] flex-col gap-7'
      ])}
    >
      <div
        className={classNames([
          navigationOutside && 'navigation-outside',
          navigationOutside &&
            slidesPerView === 'auto' &&
            'visible-other-slides',
          navigationPosY === 'bottom' &&
            'navigation-bottom',
          'mobile relative flex w-full max-w-full flex-col gap-7'
        ])}
      >
        <Swiper
          onBeforeInit={(swiper: SwiperCore): void => {
            swiperRef.current = swiper;
            setCurrentActiveSlide(swiper.activeIndex);
          }}
          onSlideChange={(swiper: SwiperCore): void => {
            setCurrentActiveSlide(swiper.activeIndex);
          }}
          centeredSlides={centeredSlides}
          modules={[Navigation, EffectCards]}
          watchSlidesProgress
          loop={loop}
          effect="cards"
          observer
          creativeEffect={{
            perspective: true
          }}
          cardsEffect={{
            slideShadows: false,
            rotate: false,
            perSlideOffset: 40
          }}
          breakpoints={{
            600: {
              cardsEffect: {
                perSlideOffset: 25
              }
            },
            320: {
              cardsEffect: {
                perSlideOffset: 40
              }
            }
          }}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slides.indexOf(slide)}>
              {slide}
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          type="button"
          className={`swiper-button-prev ${
            currentActiveSlide === 0
              ? 'swiper-button-disabled'
              : ''
          }`}
          onClick={() => {
            swiperRef.current?.slidePrev();
          }}
        >
          <Icon icon="chevron-left" />
        </button>
        <button
          type="button"
          className={`swiper-button-next ${
            currentActiveSlide === lastSlideIndex
              ? 'swiper-button-disabled'
              : ''
          }`}
          onClick={() => {
            swiperRef.current?.slideNext();
          }}
        >
          <Icon icon="chevron-right" />
        </button>
      </div>

      {utilityButton && (
        <div
          className={classNames([
            navigationPosY === 'bottom'
              ? 'lg:mt-[-5.2rem]'
              : 'lg:mt-0',
            'z-10 mx-auto mt-[-5.2rem] flex w-auto justify-center'
          ])}
        >
          <Button {...utilityButton} />
        </div>
      )}
    </div>
  );
}

function Slider({
  slides,
  navigationOutside = false,
  navigationPosY,
  utilityButton,
  slidesPerView,
  loop = false,
  centeredSlides = false
}: Props) {
  const slidesLength = slides.length;
  const lastSlideIndex = slides.length - 1;
  const [currentActiveSlide, setCurrentActiveSlide] =
    useState(0);
  const swiperRef = useRef<SwiperType>();

  return (
    <div
      className={classNames([
        'DESKTOP',
        navigationOutside && !navigationPosY && 'lg:px-16',
        navigationOutside &&
          slidesPerView === 'auto' &&
          'lg:px-0',
        navigationPosY === 'bottom' && 'lg:px-0',
        'flex w-full max-w-[1320px] flex-col gap-7'
      ])}
    >
      <div
        className={classNames([
          navigationOutside && 'navigation-outside',
          navigationOutside &&
            slidesPerView === 'auto' &&
            'visible-other-slides',
          navigationPosY === 'bottom' &&
            'navigation-bottom',
          'relative flex w-full max-w-full flex-col gap-7'
        ])}
      >
        <Swiper
          modules={[Navigation]}
          onBeforeInit={(swiper: SwiperCore): void => {
            swiperRef.current = swiper;
            setCurrentActiveSlide(swiper.activeIndex);
          }}
          onSlideChange={(swiper: SwiperCore): void => {
            setCurrentActiveSlide(swiper.activeIndex);
          }}
          observer
          centeredSlides={centeredSlides}
          slidesPerGroup={1}
          spaceBetween={30}
          watchSlidesProgress
          loop={loop}
          className={classNames([
            slidesLength <= 1 && 'no-navigation',
            'w-full max-w-full'
          ])}
          breakpoints={{
            1280: {
              slidesPerView: slidesPerView || 4
            },
            1024: {
              slidesPerView: slidesPerView || 3
            },
            768: {
              slidesPerView: slidesPerView || 2
            },
            320: {
              slidesPerView: slidesPerView || 1
            }
          }}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slides.indexOf(slide)}>
              {slide}
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          type="button"
          className={`swiper-button-prev ${
            currentActiveSlide === 0
              ? 'swiper-button-disabled'
              : ''
          }`}
          onClick={() => {
            swiperRef.current?.slidePrev();
          }}
        >
          <Icon icon="chevron-left" />
        </button>
        <button
          type="button"
          className={`swiper-button-next ${
            currentActiveSlide === lastSlideIndex
              ? 'swiper-button-disabled'
              : ''
          }`}
          onClick={() => {
            swiperRef.current?.slideNext();
          }}
        >
          <Icon icon="chevron-right" />
        </button>
      </div>

      {utilityButton != null && (
        <div
          className={classNames([
            navigationPosY === 'bottom'
              ? 'lg:mt-[-5.2rem]'
              : 'lg:mt-0',
            'z-10 mx-auto mt-[-5.2rem] flex w-auto justify-center'
          ])}
        >
          <Button {...utilityButton} />
        </div>
      )}
    </div>
  );
}

export default Slider;
